import React, { useCallback, useEffect, useMemo } from "react";
import { StyleSheet } from "react-native";
import { AbsenceInput, AbsenceType } from "../../../__generated__/graphql";
import Modal from "../../Modal";
import ScreenCard from "../../../screens/ScreenCard";
import SimpleHeader from "../../SimpleHeader";
import VStack from "../../../ui_atoms/VStack";
import { Controller, useForm } from "react-hook-form";
import TextField from "../../TextField";
import { useI18n } from "../../../hooks/i18n-hooks";
import SelectField from "../../SelectField";
import DateField from "../../DateField";
import { usePrevious } from "../../../hooks/use-previous";
import { LinkSmallText } from "../../FormattedText";
import { useIntercom } from "../../../hooks/intercom";

type FormData = {
    type?: AbsenceType | null;
    start?: string;
    end?: string;
    description?: string;
};

type AbsenceCreateModalProps = {
    onCreate: (absence: AbsenceInput) => void;
    minDate?: Date;
    maxDate?: Date;
    onClose: () => void;
    visible: boolean;
};

const AbsenceCreateModal: React.FC<AbsenceCreateModalProps> = ({
    onClose,
    onCreate,
    visible,
    minDate,
    maxDate,
}) => {
    const styles = useStyles();
    const i18n = useI18n();

    const {
        control,
        handleSubmit,
        reset,
        formState: { isValid },
    } = useForm<FormData>({
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: {
            type: null,
            start: "",
            end: "",
            description: "",
        },
    });
    const previousVisible = usePrevious(visible);
    useEffect(() => {
        if (visible && !previousVisible) {
            reset();
        }
    }, [previousVisible, reset, visible]);

    const typeItems = useMemo(() => {
        return Object.values(AbsenceType).map((type) => ({
            label: i18n.t(`absence.type.${type}`),
            value: type,
        }));
    }, [i18n]);

    const handleCreateAbsence = useCallback(
        (formData: FormData) => {
            onCreate({
                type: formData.type!,
                start: formData.start!,
                end: formData.end!,
                comment: formData.description!,
            });
        },
        [onCreate],
    );
    const { presentArticle } = useIntercom();

    return (
        <Modal visible={visible} onClose={onClose} hideCloseButton animationType="slide">
            <SimpleHeader title={i18n.t("absence.add_title")} onClose={onClose} mode="small" />
            <ScreenCard
                buttons={[
                    {
                        onPress: handleSubmit(handleCreateAbsence),
                        mode: "secondary",
                        isValid: isValid,
                    },
                ]}
            >
                <VStack gap={1} h="2/3">
                    <Controller
                        name="type"
                        control={control}
                        rules={{
                            required: true,
                        }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <SelectField
                                halfScreen
                                label={i18n.t(`absence.field_type`)}
                                value={value || undefined}
                                onSelect={(v) => onChange(v.value)}
                                error={error?.message}
                                showErrorText
                                data={typeItems}
                            />
                        )}
                    />
                    <LinkSmallText
                        onPress={() => {
                            void presentArticle("WorkingTimeTypes");
                        }}
                    >
                        {i18n.t("workingTime.dropdown_show_intercom")}
                    </LinkSmallText>
                    <>
                        <Controller
                            name="start"
                            control={control}
                            rules={{
                                required: true,
                                deps: ["end"],
                            }}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <DateField
                                    isSelfContained
                                    value={value ? new Date(value) : undefined}
                                    onChange={onChange}
                                    label={i18n.t(`absence.field_start`)}
                                    error={error?.message}
                                    style={styles.stackedFieldTop}
                                    minimumDate={minDate}
                                    maximumDate={maxDate}
                                    right="__none__"
                                    timePicker="7:30"
                                />
                            )}
                        />
                        <Controller
                            name="end"
                            control={control}
                            rules={{
                                required: true,
                                validate: (value, { start }) => {
                                    if (start && value) {
                                        if (start > value) {
                                            return i18n.t("absence.error_end_before_start");
                                        }
                                    }
                                    return undefined;
                                },
                                deps: ["start"],
                            }}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <DateField
                                    isSelfContained
                                    value={value ? new Date(value) : undefined}
                                    onChange={onChange}
                                    label={i18n.t(`absence.field_end`)}
                                    error={error?.message}
                                    style={styles.stackedFieldBottom}
                                    minimumDate={minDate}
                                    maximumDate={maxDate}
                                    right="__none__"
                                    timePicker="17:30"
                                />
                            )}
                        />
                    </>
                    <Controller
                        name="description"
                        control={control}
                        rules={{
                            required: true,
                        }}
                        render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
                            <TextField
                                label={i18n.t(`absence.field_description`)}
                                value={value}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                error={error?.message}
                                showErrorText
                                multiline
                            />
                        )}
                    />
                </VStack>
            </ScreenCard>
        </Modal>
    );
};

const useStyles = () => {
    return useMemo(
        () =>
            StyleSheet.create({
                stackedFieldBottom: {
                    borderTopRightRadius: 0,
                    borderTopLeftRadius: 0,
                    marginTop: -2,
                },
                stackedFieldTop: {
                    borderBottomRightRadius: 0,
                    borderBottomLeftRadius: 0,
                    marginTop: -2,
                },
            }),
        [],
    );
};

export default AbsenceCreateModal;
